// Single owner of the assistant chat panel's persisted UI state. All access is
// best-effort and SSR/test-safe: guarded on `window`, wrapped in try/catch, and
// validated on read so corrupt values fall back to a typed default. Keys are
// namespaced under `assistant.chat.*`; `assistant.chat.width` predates this
// module (kept for continuity with already-persisted widths).

import { STORAGE_KEYS } from './sessionStorage';

export const CHAT_WIDTH_MIN = 320;
export const CHAT_WIDTH_MAX = 720;
export const CHAT_WIDTH_DEFAULT = 480;

function readRaw(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRaw(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // best-effort only
  }
}

function removeRaw(key: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // best-effort only
  }
}

function getBool(key: string): boolean {
  return readRaw(key) === 'true';
}

function setBool(key: string, v: boolean): void {
  writeRaw(key, v ? 'true' : 'false');
}

export function getOpen(): boolean {
  return getBool(STORAGE_KEYS.CHAT_OPEN);
}
export function setOpen(v: boolean): void {
  setBool(STORAGE_KEYS.CHAT_OPEN, v);
}
export function getFullscreen(): boolean {
  return getBool(STORAGE_KEYS.CHAT_FULLSCREEN);
}
export function setFullscreen(v: boolean): void {
  setBool(STORAGE_KEYS.CHAT_FULLSCREEN, v);
}

export function getSessionId(): string | null {
  const raw = readRaw(STORAGE_KEYS.CHAT_SESSION_ID);
  return raw && raw.length > 0 ? raw : null;
}

export function setSessionId(v: string | null): void {
  if (v) writeRaw(STORAGE_KEYS.CHAT_SESSION_ID, v);
  else removeRaw(STORAGE_KEYS.CHAT_SESSION_ID);
}

export function getWidth(): number {
  const raw = readRaw(STORAGE_KEYS.CHAT_WIDTH);
  const parsed = raw == null ? NaN : parseInt(raw, 10);
  if (
    Number.isFinite(parsed) &&
    parsed >= CHAT_WIDTH_MIN &&
    parsed <= CHAT_WIDTH_MAX
  ) {
    return parsed;
  }
  return CHAT_WIDTH_DEFAULT;
}

export function setWidth(v: number): void {
  writeRaw(STORAGE_KEYS.CHAT_WIDTH, String(v));
}

/**
 * Remove the session-scoped chat keys on logout for cross-user isolation.
 * Width is a non-sensitive display preference and is intentionally kept (also
 * avoids the debounced width write racing this wipe on a shared browser).
 */
export function clearOnLogout(): void {
  removeRaw(STORAGE_KEYS.CHAT_OPEN);
  removeRaw(STORAGE_KEYS.CHAT_FULLSCREEN);
  removeRaw(STORAGE_KEYS.CHAT_SESSION_ID);
}
